// src/pages/categorias/Categorias.js
import React, { useEffect, useState } from 'react'
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import InputGroup from 'react-bootstrap/InputGroup'
import Button from 'react-bootstrap/Button'
import { toast } from 'react-toastify'
import api from 'api/api'

import './categorias.css'

import { CATEGORIAS_FETCH_CUANTOS } from 'config/categorias'
import { debounce } from 'utils/debounce'
import ListaCategorias from './ListaCategorias'
import NuevaCategoria from './NuevaCategoria'
import EditarCategoria from './EditarCategoria'
import EliminarCategoria from './EliminarCategoria'
import VerCategoria from './VerCategoria'
import TopHeader from 'components/TopHeader'

function Categorias() {
	const [fetchDeACuantos, setFetchDeACuantos] = useState(CATEGORIAS_FETCH_CUANTOS)
	const [numResultados, setNumResultados] = useState(0)
	const [paginaAct, setPaginaAct] = useState(1)
	const [loading, setLoading] = useState(false)
	const [categoriasActualizando, setCategoriasActualizando] = useState([])
	const [categoriasOptimistas, setCategoriasOptimistas] = useState(null)

	const [busqueda, setBusqueda] = useState('')
	const [filtroBusqueda, setFiltroBusqueda] = useState('')
	const [categorias, setCategorias] = useState([])
	const [todasCategorias, setTodasCategorias] = useState([])
	const [accionActual, setAccionActual] = useState({
		categoria: null,
		accion: ''
	})

	const opcionesCuantosMostrar = [10, 25, 50]

	const obtenerConfiguracion = async () => {
		try {
			setLoading(true)
			const { datos } = await api.get('a/config-jerarquias')
			setTodasCategorias(datos.jerarquias || [])
		} catch (error) {
			toast.error('Error al obtener la configuración de jerarquías')
			console.error(error)
		} finally {
			setLoading(false)
		}
	}

	const refreshCategorias = async () => {
		try {
			// Si hay categorías optimistas o categorías actualizándose, no mostrar loading general
			if (categoriasActualizando.length === 0 && !categoriasOptimistas) {
				setLoading(true)
			}

			const { datos } = await api.post('a/ver-jerarquias', {
				cuantos: fetchDeACuantos,
				desde: paginaAct === 1 ? 0 : (paginaAct - 1) * fetchDeACuantos,
				filtros: {
					nombre: [filtroBusqueda]
				}
			})

			// Al recibir los datos reales, eliminamos la versión optimista
			setCategoriasOptimistas(null)
			setCategorias(datos.jerarquias)
			setNumResultados(datos.numResultados)
		} catch (error) {
			toast.error('Error de conexión')
			console.error(error)
			// En caso de error, también descartamos la versión optimista
			setCategoriasOptimistas(null)
		} finally {
			setLoading(false)
		}
	}

	const handleBusqueda = (value) => {
		if (value) {
			setFiltroBusqueda(value)
		} else {
			setFiltroBusqueda('')
		}
	}

	const debouceFiltroBusqueda = React.useCallback(
		debounce(handleBusqueda, 400),
		[]
	)

	useEffect(() => {
		debouceFiltroBusqueda(busqueda)
	}, [busqueda])

	useEffect(() => {
		if (paginaAct === 1) {
			refreshCategorias()
		} else {
			setPaginaAct(1)
		}
	}, [filtroBusqueda, fetchDeACuantos])

	useEffect(() => {
		if (!loading) {
			refreshCategorias()
		}
	}, [paginaAct])

	const subirArchivo = async (archivo) => {
		// Primero sube el archivo, luego crea la categoría
		const serverData = new FormData()
		serverData.append('foto', archivo)
		let multimediaId = null

		try {
			const { datos, msg } = await api.post('a/multimedia', serverData)
			multimediaId = datos.id
			return multimediaId
		} catch (error) {
			toast.error('Error al subir la imagen')
			return null
		}
	}

	const nuevaCategoria = async (data) => {
		try {
			// Extraer atributos del data antes de enviar
			const atributosTipoIds = data.atributosTipoIds || []
			delete data.atributosTipoIds

			// Si hay una imagen, primero la subimos
			if (data.archivo) {
				const multimediaId = await subirArchivo(data.archivo)
				if (multimediaId) {
					data.multimediaId = multimediaId
				}
				delete data.archivo
			}

			// Crear la categoría - el backend devuelve el objeto completo en 'datos'
			const { datos, msg } = await api.post('a/jerarquias', data)

			// Si se creó correctamente y hay atributos seleccionados, asignarlos
			// 'datos' es el objeto completo de la jerarquía, que incluye 'id'
			if (datos && datos.id && atributosTipoIds.length > 0) {
				try {
					await api.put(`a/jerarquias/${datos.id}/atributos`, {
						atributosTipoIds: atributosTipoIds
					})
				} catch (errorAtributos) {
					console.error('Error al asignar atributos:', errorAtributos)
					toast.warning('Categoría creada, pero hubo un error al asignar los atributos')
				}
			}

			refreshCategorias()
			cerrarAccion()
			toast.success(msg)
		} catch (error) {
			console.error(error)
			toast.error('Error de conexión')
		}
	}

	const editarCategoria = async (data) => {
		if (Object.keys(data).length) {
			try {
				// Si hay una imagen, primero la subimos
				if (data.archivo) {
					const multimediaId = await subirArchivo(data.archivo)
					if (multimediaId) {
						data.multimediaId = multimediaId
					}
					delete data.archivo
				}

				const enviar = {
					...data,
					id: accionActual.categoria.id
				}

				const { msg } = await api.put('a/jerarquias/' + accionActual.categoria.id, enviar)
				refreshCategorias()
				cerrarAccion()
				toast.success(msg)
			} catch (error) {
				console.error(error)
				toast.error('Error de conexión')
			}
		}
	}

	const eliminarCategoria = async (id) => {
		try {
			const { msg } = await api.delete('a/jerarquias/' + id)
			refreshCategorias()
			cerrarAccion()
			toast.success(msg)
		} catch (error) {
			console.error(error)
			toast.error('Error de conexión')
		}
	}

	// Encontrar categoría por ID (función auxiliar)
	const encontrarCategoriaPorId = (lista, id) => {
		for (const cat of lista) {
			if (cat.id === id) return cat

			if (cat.subcategorias && cat.subcategorias.length > 0) {
				const encontrado = encontrarCategoriaPorId(cat.subcategorias, id)
				if (encontrado) return encontrado
			}
		}
		return null
	}

	// Función auxiliar para obtener hermanos de una categoría
	const obtenerHermanos = (categoria) => {
		if (!categoria) return []

		if (!categoria.jerarquiaPapa || !categoria.jerarquiaPapa.id) {
			// Es categoría principal, sus hermanos son otras categorías principales
			return categorias.filter(c =>
				!c.jerarquiaPapa || !c.jerarquiaPapa.id
			)
		}

		// Buscar el padre y sus hijos (hermanos de la categoría)
		const padreId = categoria.jerarquiaPapa.id
		const padre = encontrarCategoriaPorId(categorias, padreId)

		if (padre && padre.subcategorias) {
			return padre.subcategorias
		}

		return []
	}

	// Función completamente rediseñada para crear copias optimistas
	// Función optimizada para crear copias optimistas con comportamiento unificado
	const crearCopiaOptimistaCategorias = (categoriasOriginales, cambios, tipoOperacion) => {
		// Función auxiliar para clonar profundamente el árbol
		const cloneTree = (tree) => JSON.parse(JSON.stringify(tree))

		// Función auxiliar para encontrar categoría por ID
		const encontrarCategoriaPorId = (categorias, id) => {
			for (const cat of categorias) {
				if (cat.id === id) return cat

				if (cat.subcategorias && cat.subcategorias.length > 0) {
					const encontrada = encontrarCategoriaPorId(cat.subcategorias, id)
					if (encontrada) return encontrada
				}
			}
			return null
		}

		// Crear una copia profunda del árbol original
		const arbolOriginal = cloneTree(categoriasOriginales)

		if (tipoOperacion === 'reordenar') {
			// Para reordenar, vamos a recrear el árbol completo
			// cambios[0] es la categoría que se mueve y cambios[1] es donde se suelta
			const catMovidaId = cambios[0]?.id
			const catObjetivoId = cambios[1]?.id

			if (!catMovidaId || !catObjetivoId) {
				console.error('No se proporcionaron IDs suficientes para reordenar')
				return arbolOriginal
			}

			// Encontrar ambas categorías para identificar el nivel
			const catMovida = encontrarCategoriaPorId(arbolOriginal, catMovidaId)
			const catObjetivo = encontrarCategoriaPorId(arbolOriginal, catObjetivoId)

			if (!catMovida || !catObjetivo) {
				console.error('No se encontraron las categorías para reordenar')
				return arbolOriginal
			}

			// Verificar que ambas categorías están en el mismo nivel
			const sonMismoNivel =
				(catMovida.jerarquiaPapa?.id === catObjetivo.jerarquiaPapa?.id) ||
				(!catMovida.jerarquiaPapa && !catObjetivo.jerarquiaPapa)

			if (!sonMismoNivel) {
				console.error('Las categorías no están en el mismo nivel')
				return arbolOriginal
			}

			// Función para recrear un nivel con la categoría reordenada
			const reordenarNivel = (categorias, catMovidaId, catObjetivoId) => {
				// Primero creamos copias de todas las categorías
				const categoriasCopia = categorias.map(cat => {
					// Copia profunda de la categoría
					const catCopia = { ...cat }

					// Procesar subcategorías si existen
					if (cat.subcategorias && cat.subcategorias.length > 0) {
						catCopia.subcategorias = reordenarNivel(cat.subcategorias, catMovidaId, catObjetivoId)
					} else {
						catCopia.subcategorias = []
					}

					return catCopia
				})

				// Verificar si este nivel contiene las categorías a reordenar
				const contieneMovida = categoriasCopia.some(c => c.id === catMovidaId)
				const contieneObjetivo = categoriasCopia.some(c => c.id === catObjetivoId)

				// Si ambas categorías están en este nivel, reordenar
				if (contieneMovida && contieneObjetivo) {
					// Extraer la categoría movida
					const catMovida = categoriasCopia.find(c => c.id === catMovidaId)

					// Crear un nuevo array sin la categoría movida
					const arraySinMovida = categoriasCopia.filter(c => c.id !== catMovidaId)

					// Encontrar la posición de la categoría objetivo
					const indexObjetivo = arraySinMovida.findIndex(c => c.id === catObjetivoId)

					// Crear el resultado reordenado: insertar DESPUÉS del objetivo
					const resultado = [
						...arraySinMovida.slice(0, indexObjetivo + 1), // Hasta e incluyendo el objetivo
						catMovida, // Insertar la categoría movida
						...arraySinMovida.slice(indexObjetivo + 1) // El resto de categorías
					]

					return resultado
				}

				// Si este nivel no contiene las categorías, devolver sin cambios
				return categoriasCopia
			}

			// Función para recorrer el árbol y encontrar el nivel a reordenar
			const procesarArbol = (categorias) => {
				// Verificar si este nivel contiene las categorías a reordenar
				const contieneMovida = categorias.some(c => c.id === catMovidaId)
				const contieneObjetivo = categorias.some(c => c.id === catObjetivoId)

				// Si ambas categorías están en este nivel, reordenar
				if (contieneMovida && contieneObjetivo) {
					return reordenarNivel(categorias, catMovidaId, catObjetivoId)
				}

				// Si no, buscar recursivamente en los niveles inferiores
				return categorias.map(cat => {
					// Copia profunda de la categoría
					const catCopia = { ...cat }

					// Procesar subcategorías si existen
					if (cat.subcategorias && cat.subcategorias.length > 0) {
						catCopia.subcategorias = procesarArbol(cat.subcategorias)
					} else {
						catCopia.subcategorias = []
					}

					return catCopia
				})
			}

			// Procesar el árbol completo
			return procesarArbol(arbolOriginal)
		}
		else if (tipoOperacion === 'mover') {
			// Obtener datos del movimiento
			const { id: catId, nuevoPadreId, nuevoOrden } = cambios[0]

			// Encontrar la categoría a mover
			const catAMover = encontrarCategoriaPorId(arbolOriginal, catId)
			if (!catAMover) {
				console.error('No se encontró la categoría a mover:', catId)
				return arbolOriginal
			}

			// Encontrar el nuevo padre (si existe)
			let nuevoPadre = null
			if (nuevoPadreId !== null) {
				nuevoPadre = encontrarCategoriaPorId(arbolOriginal, nuevoPadreId)
				if (!nuevoPadre) {
					console.error('No se encontró la categoría padre destino:', nuevoPadreId)
					return arbolOriginal
				}
			}

			// Función para eliminar la categoría del árbol original
			const eliminarCategoria = (categorias, idEliminar) => {
				return categorias.map(cat => {
					// Si es la categoría a eliminar, no la incluimos
					if (cat.id === idEliminar) {
						return null
					}

					// Copia de la categoría
					const catCopia = { ...cat }

					// Procesar subcategorías si existen
					if (cat.subcategorias && cat.subcategorias.length > 0) {
						// Filtrar las subcategorías eliminando la que vamos a mover
						catCopia.subcategorias = eliminarCategoria(cat.subcategorias, idEliminar)
							.filter(Boolean) // Eliminar los null
					} else {
						catCopia.subcategorias = []
					}

					return catCopia
				}).filter(Boolean) // Eliminar los null
			}

			// Crear una copia profunda de la categoría a mover
			const catMovidaCopia = cloneTree(catAMover)

			// Eliminar la categoría del árbol original
			const arbolSinCategoria = eliminarCategoria(arbolOriginal, catId)

			// Función para insertar la categoría en su nuevo padre
			const insertarEnPadre = (categorias, idPadre, categoriaInsertar) => {
				return categorias.map(cat => {
					// Copia de la categoría
					const catCopia = { ...cat }

					// Si es el padre destino, añadir la categoría a sus subcategorías
					if (cat.id === idPadre) {
						// Actualizar referencia al padre
						const categoriaConPadre = {
							...categoriaInsertar,
							jerarquiaPapa: {
								id: cat.id,
								nombre: cat.nombre
							}
						}

						// Añadir a las subcategorías
						catCopia.subcategorias = [
							...(cat.subcategorias || []),
							categoriaConPadre
						]
					}
					// Si no es el padre, procesar sus subcategorías
					else if (cat.subcategorias && cat.subcategorias.length > 0) {
						catCopia.subcategorias = insertarEnPadre(cat.subcategorias, idPadre, categoriaInsertar)
					} else {
						catCopia.subcategorias = []
					}

					return catCopia
				})
			}

			// Si el nuevo padre es null, añadir como categoría principal
			if (nuevoPadreId === null) {
				// Actualizar referencia al padre
				catMovidaCopia.jerarquiaPapa = null

				// Añadir al nivel principal
				return [...arbolSinCategoria, catMovidaCopia]
			} else {
				// Insertar en el nuevo padre
				return insertarEnPadre(arbolSinCategoria, nuevoPadreId, catMovidaCopia)
			}
		}

		// Si no es ninguna operación reconocida, devolver el árbol original
		return arbolOriginal
	}

	const reordenarCategorias = async (categoriasOrdenadas, reordenamiento) => {
		try {
			// Identificar las categorías afectadas para mostrar loading solo en ellas
			const idsAfectados = categoriasOrdenadas.map(cat => cat.id)
			setCategoriasActualizando(idsAfectados)

			// Crear una copia optimista de las categorías actuales
			// Pasamos también el reordenamiento para ubicar visualmente las categorías
			const copiaOptimista = crearCopiaOptimistaCategorias(
				categorias,
				reordenamiento || categoriasOrdenadas,
				'reordenar'
			)
			setCategoriasOptimistas(copiaOptimista)

			// Hacer la petición al servidor
			const { msg } = await api.post('a/reordenar-jerarquias', {
				categoriasOrdenadas
			})

			// Al recibir respuesta exitosa, volver al estado normal
			refreshCategorias()
			toast.success(msg)
		} catch (error) {
			console.error(error)
			toast.error('Error al reordenar las categorías: ' + (error.message || error))
			// En caso de error, descartamos la versión optimista
			setCategoriasOptimistas(null)
		} finally {
			// Limpiar el estado de categorías en actualización
			setCategoriasActualizando([])
		}
	}

	const moverCategoria = async (categoriaId, nuevoPadreId, nuevoOrden) => {
		try {
			// Identificar la categoría que se está moviendo
			const catMovida = encontrarCategoriaPorId(categorias, categoriaId)
			if (!catMovida) return

			// Crear un conjunto de IDs afectados para mostrar loading
			const idsAfectados = [categoriaId]
			setCategoriasActualizando(idsAfectados)

			// OPCIÓN SIMPLIFICADA: No crear copia optimista para cambios de padre
			// Ya que es complejo visualizar correctamente y puede causar confusión
			setCategoriasOptimistas(null)

			// Hacer la petición al servidor
			const { msg } = await api.post('a/mover-jerarquia', {
				categoriaId,
				nuevoPadreId,
				nuevoOrden
			})

			// Al recibir respuesta exitosa, actualizar con los datos reales
			refreshCategorias()
			toast.success(msg)
		} catch (error) {
			console.error(error)
			toast.error('Error al mover la categoría: ' + (error.message || error))
		} finally {
			setCategoriasActualizando([])
		}
	}

	useEffect(() => {
		if (!loading) {
			refreshCategorias()
			obtenerConfiguracion()
		}
	}, [])

	// Las acciones específicas para la lista de categorias
	const accionesActuales = {
		handleVer: (categoria) => {
			setAccionActual({
				categoria,
				accion: 'ver'
			})
		},
		handleEditar: (categoria) => {
			setAccionActual({
				categoria,
				accion: 'editar'
			})
		},
		handleEliminar: (categoria) => {
			setAccionActual({
				categoria,
				accion: 'eliminar'
			})
		},
		handleReordenar: reordenarCategorias,
		handleMover: moverCategoria
	}

	const cerrarAccion = () => {
		setAccionActual({
			categoria: null,
			accion: ''
		})
	}

	// render
	return (
		<Container fluid id="categorias" className="vh-100">
			<TopHeader
				iconName="tags"
				text="Categorías"
			/>
			<Row className="d-flex no-gutters">
				<Col className="mt-2">
					<Form>
						<Row id="searchbar" className="justify-content-between align-items-center">
							<Col sm={6} md={3} style={{ display: 'block' }}>
								<small>
									<span className="d-none d-sm-inline-block">Mostrar de a</span>
									<Form.Group style={{ display: 'inline-block' }}>
										<Form.Select
											size="sm"
											onChange={(e) => setFetchDeACuantos(e.target.value)}
											className="mx-1"
											disabled={categoriasActualizando.length > 0}
										>
											{opcionesCuantosMostrar.map((o) => {
												return (
													<option value={o} key={`mostrar-${o}`}>
														{o}
													</option>
												)
											})}
										</Form.Select>
									</Form.Group>
									<span className="ms-2">categorías</span>
								</small>
							</Col>
							<Col md={5} lg={4}>
								<Form.Group controlId="txtBusqueda">
									<InputGroup>
										<Form.Control
											autoFocus
											value={busqueda}
											onChange={(e) => setBusqueda(e.target.value)}
											type="text"
											placeholder="Buscar"
											aria-label="Buscar"
											disabled={categoriasActualizando.length > 0}
										/>
										<InputGroup.Text><Form.Label className="mb-0"><i className="fa fa-search"></i></Form.Label></InputGroup.Text>
									</InputGroup>
								</Form.Group>
							</Col>
						</Row>
					</Form>
				</Col>
			</Row>
			<Row className="mb-3">
				<Col>
					<Button
						variant="primary"
						onClick={() => setAccionActual({
							categoriaId: null,
							accion: 'nuevo'
						})}
						className="mt-2"
						disabled={categoriasActualizando.length > 0}
					>
						<i className="fa fa-plus"></i> Nueva categoría
					</Button>
				</Col>
			</Row>
			{/* Lista categorias */}
			<ListaCategorias
				loading={loading}
				categorias={categoriasOptimistas || categorias}
				numResultados={numResultados}
				pagina={paginaAct}
				fetchDeACuantos={fetchDeACuantos}
				cambiarPagina={setPaginaAct}
				acciones={accionesActuales}
				categoriasActualizando={categoriasActualizando}
			/>
			{/* Panel Nuevo */}
			<NuevaCategoria
				mostrar={accionActual.accion === 'nuevo'}
				guardar={nuevaCategoria}
				cancelar={cerrarAccion}
				categoriasDisponibles={todasCategorias}
			/>
			{/* Panel Ver */}
			<VerCategoria
				mostrar={accionActual.accion === 'ver'}
				categoria={accionActual.categoria}
				cancelar={cerrarAccion}
			/>
			{/* Panel Editar */}
			<EditarCategoria
				mostrar={accionActual.accion === 'editar'}
				categoriaOriginal={accionActual.categoria}
				cancelar={cerrarAccion}
				guardar={editarCategoria}
				categoriasDisponibles={todasCategorias}
			/>
			{/* Modal Delete */}
			<EliminarCategoria
				mostrar={accionActual.accion === 'eliminar'}
				categoria={accionActual.categoria}
				cancelar={cerrarAccion}
				eliminar={eliminarCategoria}
			/>
		</Container>
	)
}

export default Categorias